import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { api } from '../api';
import * as XLSX from 'xlsx';

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [productTypes, setProductTypes] = useState([]);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);

  const [modalContent, setModalContent] = useState(null);

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }

    const fetchData = async () => {
      try {
        const [types, allProducts, allOrders] = await Promise.all([
          api.getProductTypes(),
          api.getAllProducts(),
          api.getOrders(),
        ]);

        let filteredOrders = allOrders;
        if (user.role !== 'admin') {
          filteredOrders = allOrders.filter(o => o.person === user.username);
        }

        setProductTypes(types);
        setProducts(allProducts);
        setOrders(filteredOrders);
      } catch (err) {
        console.error('Error fetching dashboard data:', err.message);
      }
    };

    fetchData();
  }, [user, navigate]);

  const lastAdded = [...products].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];
  const recentOrders = [...orders].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 5);

  const handleModalClose = () => setModalContent(null);

  const showProductTypesModal = () => {
    const typeMap = productTypes.map(type => {
      const filtered = products.filter(p => p.type === type);
      return (
        <div key={type} className="mb-3">
          <h3 className="font-semibold text-[#3C3B3F]">{type}</h3>
          <ul className="list-disc list-inside ml-4 text-sm text-[#6B6B6B]">
            {filtered.map(p => (
              <li key={p.code}>{p.name} - Factory Name: {p.subType}</li>
            ))}
          </ul>
        </div>
      );
    });

    setModalContent(
      <div>
        <h2 className="text-2xl font-semibold mb-2 text-[#3C3B3F]">Product Types & Products</h2>
        {typeMap}
      </div>
    );
  };

  const showProductsModal = () => {
    const exportData = products.map(p => ({
      Type: p.type,
      Name: p.name,
      Code: p.code,
      Quality: p.quality,
      Quantity: p.quantity
    }));

    const rows = products.map(p => (
      <tr key={p.code}>
        <td className="border px-2 py-1">{p.type}</td>
        <td className="border px-2 py-1">{p.name}</td>
        <td className="border px-2 py-1">{p.code}</td>
        <td className="border px-2 py-1">{p.subType}</td>
      </tr>
    ));

    setModalContent(
      <div>
        <div className="flex justify-between items-center mb-2">
          <h2 className="text-2xl font-semibold text-[#3C3B3F]">All Products</h2>
          <button
            className="px-3 py-1 bg-[#3C3B3F] text-white rounded text-sm hover:bg-[#2E2D30] transition"
            onClick={() => exportToExcel(exportData, 'products.xlsx')}
          >
            Export to Excel
          </button>
        </div>
        <table className="w-full text-sm border border-[#ddd]">
          <thead className="bg-[#E7E2DB]">
            <tr>
              <th className="border px-2 py-1 text-[#3C3B3F]">Type</th>
              <th className="border px-2 py-1 text-[#3C3B3F]">Name</th>
              <th className="border px-2 py-1 text-[#3C3B3F]">Code</th>
              <th className="border px-2 py-1 text-[#3C3B3F]">Factory Name</th>
            </tr>
          </thead>
          <tbody>{rows}</tbody>
        </table>
      </div>
    );
  };

  const showOrdersModal = () => {
    const exportData = orders.map(order => ({
      'Order ID': order.parentOrderId,
      'Created At': new Date(order.date).toLocaleDateString('en-CA'),
      'Product Name': order.productName,
      'Code': order.productCode,
      'Weight': order.weight || 'N/A',
      'Size': order.size || 'N/A',
      'Color': order.color || 'N/A',
      'Qty': order.quantity,
      'Person': order.person,
      'FactoryName': order.subType || 'Unknown',
      'Expected Date': order.address ? order.address : 'N/A',
      'Status': order.status || 'Pending',
    }));

    const rows = orders.map(o => (
      <tr key={o.id}>
        <td className="border px-2 py-1">{o.parentOrderId}</td>
        <td className="border px-2 py-1">{new Date(o.date).toLocaleDateString('en-CA')}</td>
        <td className="border px-2 py-1">{o.productName}</td>
        <td className="border px-2 py-1">{o.productCode}</td>
        <td className="border px-2 py-1">{o.weight || 'N/A'}</td>
        <td className="border px-2 py-1">{o.size || 'N/A'}</td>
        <td className="border px-2 py-1">{o.color || 'N/A'}</td>
        <td className="border px-2 py-1">{o.quantity}</td>
        <td className="border px-2 py-1">{o.person}</td>
        <td className="border px-2 py-1">{o.subType || 'Unknown'}</td>
        <td className="border px-2 py-1">{o.address ? o.address : 'N/A'}</td>
        <td className="border px-2 py-1">{o.status || 'Pending'}</td>
      </tr>
    ));

    setModalContent(
      <div>
        <div className="flex justify-between items-center mb-2">
          <h2 className="text-2xl font-semibold text-[#3C3B3F]">All Orders</h2>
          <button
            className="px-3 py-1 bg-[#3C3B3F] text-white rounded text-sm hover:bg-[#2E2D30] transition"
            onClick={() => exportToExcel(exportData, 'orders.xlsx')}
          >
            Export to Excel
          </button>
        </div>
        <table className="w-full text-sm border border-[#ddd]">
          <thead className="bg-[#E7E2DB]">
            <tr>
              <th className="border px-2 py-1 text-[#3C3B3F]">Order ID</th>
              <th className="border px-2 py-1 text-[#3C3B3F]">Created At</th>
              <th className="border px-2 py-1 text-[#3C3B3F]">Product Name</th>
              <th className="border px-2 py-1 text-[#3C3B3F]">Code</th>
              <th className="border px-2 py-1 text-[#3C3B3F]">Weight</th>
              <th className="border px-2 py-1 text-[#3C3B3F]">Size</th>
              <th className="border px-2 py-1 text-[#3C3B3F]">Color</th>
              <th className="border px-2 py-1 text-[#3C3B3F]">Qty</th>
              <th className="border px-2 py-1 text-[#3C3B3F]">Person</th>
              <th className="border px-2 py-1 text-[#3C3B3F]">FactoryName</th>
              <th className="border px-2 py-1 text-[#3C3B3F]">Expected Date</th>
              <th className="border px-2 py-1 text-[#3C3B3F]">Status</th>
            </tr>
          </thead>
          <tbody>{rows}</tbody>
        </table>
      </div>
    );
  };

  const exportToExcel = (data, filename = 'data.xlsx') => {
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    XLSX.writeFile(wb, filename);
  };

  const COLORS = [
    '#A89F91', '#8B7D6B', '#D4AF37', '#C1B497', '#7E685A',
    '#BFA6A0', '#A38870', '#CBB994', '#E5D5B8', '#8C7B6B'
  ];

  const userOrderCounts = orders.reduce((acc, order) => {
    acc[order.person] = (acc[order.person] || 0) + 1;
    return acc;
  }, {});

  const pieChartData = Object.entries(userOrderCounts).map(([name, value]) => ({
    name,
    value,
  }));

  if (!user) return null;

  return (
    <div className="min-h-screen bg-[#F4F1EE] p-6">
      <h1 className="text-4xl font-bold text-[#3C3B3F] mb-8 text-center">Dashboard</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        <div
          className="bg-white p-6 rounded-2xl shadow-md cursor-pointer hover:shadow-lg transition border border-[#ddd]"
          onClick={showProductTypesModal}
        >
          <h2 className="text-xl font-semibold text-[#3C3B3F] mb-2">Product Types</h2>
          <p className="text-4xl font-bold text-[#A89F91]">{productTypes.length}</p>
        </div>

        <div
          className="bg-white p-6 rounded-2xl shadow-md cursor-pointer hover:shadow-lg transition border border-[#ddd]"
          onClick={showProductsModal}
        >
          <h2 className="text-xl font-semibold text-[#3C3B3F] mb-2">Total Products</h2>
          <p className="text-4xl font-bold text-[#A89F91]">{products.length}</p>
        </div>

        <div
          className="bg-white p-6 rounded-2xl shadow-md cursor-pointer hover:shadow-lg transition border border-[#ddd]"
          onClick={showOrdersModal}
        >
          <h2 className="text-xl font-semibold text-[#3C3B3F] mb-2">Total Orders</h2>
          <p className="text-4xl font-bold text-[#A89F91]">{orders.length}</p>
        </div>

        <div className="bg-white p-6 rounded-2xl shadow-md col-span-full border border-[#ddd]">
          <h2 className="text-xl font-semibold mb-4 text-[#3C3B3F]">Recent Orders</h2>
          <ul className="space-y-1 text-[#6B6B6B] text-sm">
            {recentOrders.map(order => (
              <li key={order.id} className="border-b border-[#ddd] pb-1">
                <strong>{order.productName}</strong> - {order.quantity} pcs on {new Date(order.date).toLocaleDateString()} at {order.subType}
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-white p-6 rounded-2xl shadow-md border border-[#ddd]">
          <h2 className="text-xl font-semibold text-[#3C3B3F] mb-2">Last Added Product</h2>
          {lastAdded ? (
            <p className="text-[#6B6B6B]">{lastAdded.name}</p>
          ) : (
            <p className="text-[#6B6B6B]">No products added</p>
          )}
        </div>

        <div className="bg-white p-6 rounded-2xl shadow-md col-span-full border border-[#ddd]">
          <h2 className="text-xl font-semibold mb-4 text-[#3C3B3F]">Orders by User</h2>
          {pieChartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={pieChartData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  fill="#8884d8"
                  label
                >
                  {pieChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ backgroundColor: '#f4f1ee', borderRadius: '8px', border: 'none', color: '#3C3B3F' }}
                  itemStyle={{ color: '#3C3B3F' }}
                />
                <Legend
                  verticalAlign="bottom"
                  height={36}
                  wrapperStyle={{ color: '#3C3B3F' }}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-[#6B6B6B]">No orders available for pie chart.</p>
          )}
        </div>
      </div>

      {/* Modal */}
      {/* Modal */}
      {modalContent && (
        <div
          className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50"
          onClick={handleModalClose}  // <-- click on backdrop closes modal
        >
          <div
            className="bg-white p-6 rounded shadow-lg max-w-3xl w-full overflow-y-auto max-h-[80vh] relative"
            onClick={e => e.stopPropagation()} // <-- stop click inside modal from bubbling up
          >
            <button
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-800"
              onClick={handleModalClose}
              aria-label="Close modal"
            >
              &times;
            </button>
            {modalContent}
          </div>
        </div>
      )}

    </div>
  );
}
